/**
 * A query in words — the one reading every surface shows.
 *
 * The chips under a list, a recent-filter chip's label, a saved view's
 * tooltip: all of them are the same question ("what does this `?q=` say?") and
 * they must not answer it three different ways. This is that answer: one
 * entry per condition, EXCEPT where two adjacent conditions are a period, in
 * which case they are one entry ("Due: This week") — the same recognizer the
 * builder's rows use, so what you see in a chip is what you get in a row.
 *
 * `indices` are positions in the query's top-level condition list, kept so a
 * chip can dismiss exactly what it stands for and no chip beside it shifts.
 */
import {
	conditionValueLabel,
	describeCondition,
	describeFacetCondition,
	findField,
	isSpanRow,
	parseForBuilder,
	rowCondition,
	topLevelConditions,
} from './kqlBuilder';
import { isFacetField } from './facets';
import { groupSpanConditions, spanLabel } from './datePresets';
import { describeFilterGroups, readFilterGroups } from './filterGroups';
import { serialize } from './kql';

const HEX = /^[0-9a-f]{24}$/i;

/**
 * A REFERENCE value (a card, an element set) in words, or undefined for any
 * other field. Named by id since deterministic filters, and its name is in the
 * vocabulary: read there first, so a lookup resolver's "…" never stands in
 * for it. An id the vocabulary does not hold says what it is without a name —
 * "a removed card" when the server said so (`removed`, from X-Query-Warnings),
 * else "a card not in this collection" (one taken off it still matches its
 * records here). The chips and the drift caution's quoted condition read the
 * same words.
 */
export function referenceValueLabel(field, raw, removed = null) {
	if (!field?.reference) return undefined;
	const named = conditionValueLabel(field, raw);
	if (named !== undefined) return named;
	if (!HEX.test(String(raw || ''))) return undefined;
	const gone = removed instanceof Set ? removed : new Set(removed || []);
	const noun = field.reference === 'set' ? 'element set' : 'card';
	if (gone.has(String(raw).toLowerCase())) return `a removed ${noun}`;
	return noun === 'card' ? 'a card not in this collection' : 'an element set not in this collection';
}

/**
 * `{ ast, advanced, sentence, entries }` — `advanced` is the whole serialized
 * query for a shape that is not one chip per condition (an `or`, a `not`,
 * parentheses), in which case `entries` is empty; `sentence` is that query in
 * words when the builder shows it as one level of groups (KPR-978: "Status is
 * Open or (Priority is Critical and Due: This week)"), else ''. `ast` is null
 * when the query does not parse at all.
 */
export function summarizeQuery(query, fields = [], { now, resolveValueLabel, removedIds = null } = {}) {
	const { ast } = parseForBuilder(query);
	if (!ast) return { ast: null, advanced: '', sentence: '', entries: [] };

	// The last argument is a vocabulary to read the path in: the columns of a
	// repeating group when a `has an entry where…` is described (KPR-683).
	const labelFor = (path, forceElement, facet, vocabulary) => findField(vocabulary || fields, path, forceElement, facet).label;
	// An element REFERENCE names another field of the same record. The chip has
	// to read "On hand <= {{Reorder at}}" — the target's own label — because
	// the query itself carries the slug, and a chip printing `{{reorder-at}}`
	// would be the one place on the platform a raw element name leaks out.
	const refLabelFor = name => findField(fields, [name]).label;
	const removed = removedIds instanceof Set ? removedIds : new Set(removedIds || []);
	const valueLabelFor = (path, forceElement, raw, facet, vocabulary) => {
		const field = findField(vocabulary || fields, path, forceElement, facet);
		const reference = referenceValueLabel(field, raw, removed);
		if (reference !== undefined) return reference;
		// A caller may know something the field does not — the TITLE behind a
		// lookup's stored id, which lives on the server, not in the vocabulary.
		const supplied = typeof resolveValueLabel === 'function' ? resolveValueLabel(field, raw) : undefined;
		if (supplied !== undefined && supplied !== null && supplied !== '') return supplied;
		return conditionValueLabel(field, raw);
	};
	/** One condition in words, the chip's own reading of it. */
	const describeNode = (node, field) => (isFacetField(field)
		// A facet reads as the narrowing it is ("People: Alice"), not as a
		// comparison on a field nobody named that.
		? describeFacetCondition(field, node, raw => valueLabelFor(node.path, node.forceElement, raw, true))
		: describeCondition(node, labelFor, valueLabelFor, refLabelFor, () => field));

	const conditions = topLevelConditions(ast);
	if (conditions === null) {
		// One level of groups reads as one sentence, each row as its chip would.
		const tree = readFilterGroups(ast, { fields });
		const sentence = tree ? describeFilterGroups(tree, (row) => {
			const field = findField(fields, row.path, row.forceElement, row.facet);
			if (isSpanRow(row)) return `${field.label}: ${spanLabel(row, now)}`;
			const node = rowCondition(row);
			return node ? describeNode(node, field) : '';
		}) : '';
		return { ast, advanced: serialize(ast), sentence, entries: [] };
	}

	const entries = groupSpanConditions(conditions, now).map(group => {
		const field = findField(fields, group.node.path, group.node.forceElement, group.node.facet);
		const describe = () => describeNode(group.node, field);
		return {
			indices: group.indices,
			path: group.node.path,
			facet: !!group.node.facet,
			// The condition itself, for a surface that draws its value as
			// something richer than words (a card filter's badges).
			node: group.node,
			// A period reads as the thing it is — "Due: This week" — not as the
			// two comparisons that implement it.
			text: group.span
				? `${labelFor(group.node.path, group.node.forceElement, group.node.facet)}: ${group.span.label}`
				: describe(),
		};
	});

	return { ast, advanced: '', sentence: '', entries };
}

/** The whole query as one line: "Status is Open · Due: This week". */
export function describeQuery(query, fields = [], options) {
	const { advanced, sentence, entries } = summarizeQuery(query, fields, options);
	if (advanced) return sentence || advanced;
	return entries.map(entry => entry.text).join(' · ');
}
